import { useContext, useMemo } from "react";
import { UserContext } from "../pages/UserContext";
import { ENTITY_TYPE_AGENCY, ENTITY_TYPE_VENDOR } from "../utlities/core.constants";

/**
 * Resolves the current user's owning organization (Vendor or Agency/Buyer) in one place so
 * components and product/report flows stop repeating `isVendorUser ? vendorId : agencyId`.
 *
 * Returns the org id/type/name plus convenience flags and `ownerIdField`, the request payload
 * key (`vendorId` or `agencyId`) the API expects when creating products/drafts/reports.
 */
export default function useCurrentOrg() {
  const { userEntity, isVendorUser, isAgencyUser, isSuperUser } = useContext(UserContext);

  return useMemo(() => {
    const type = userEntity?.type ?? null;
    const id = userEntity?.entity?.id ?? null;
    const name = userEntity?.entity?.name ?? null;
    const isAgency = type === ENTITY_TYPE_AGENCY;
    const isVendor = type === ENTITY_TYPE_VENDOR;

    return {
      id,
      type,
      name,
      isVendor,
      isAgency,
      isVendorUser: !!isVendorUser,
      isAgencyUser: !!isAgencyUser,
      isSuperUser: !!isSuperUser,
      // Whether the user belongs to any owning org (can author products/ACRs for it).
      hasOrg: Boolean(id) && (isVendor || isAgency),
      // The owner payload key the API expects for create/draft/report requests.
      ownerIdField: isAgency ? "agencyId" : "vendorId",
      // Ready-to-spread owner payload for product create/draft/update requests: sets this org's id
      // on the correct field and explicitly nulls the other, so the API never reads it as an
      // attempt to reassign the product to a different organization.
      ownerPayload: { vendorId: isAgency ? null : id, agencyId: isAgency ? id : null },
    };
  }, [userEntity, isVendorUser, isAgencyUser, isSuperUser]);
}
